/**
 * International Fishery Management Areas data layer. Includes RFMO sub-checkboxes (IATTC, ICCAT, IPHC, WCPFC).
 * Syncs from layout so layer shows when provided in initialLayers even before tab is opened.
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import type { RegistryComponentProps } from '../../../pages/registry/componentRegistry';
import { useDataLayer } from '../hooks/useDataLayer';

const RFMO_SLUGS = ['iattc', 'iccat', 'iphc', 'wcpfc'] as const;
type RfmoSlug = (typeof RFMO_SLUGS)[number];

const RFMO_DEFS: Record<
  RfmoSlug,
  { name: string; description: string }
> = {
  iattc: {
    name: 'IATTC',
    description:
      'Inter-American Tropical Tuna Commission management area',
  },
  iccat: {
    name: 'ICCAT',
    description:
      'International Commission for the Conservation of Atlantic Tunas management area',
  },
  iphc: {
    name: 'IPHC',
    description:
      'International Pacific Halibut Commission management area',
  },
  wcpfc: {
    name: 'WCPFC',
    description:
      'Western and Central Pacific Fisheries Commission management area',
  },
};

interface RfmoState {
  iattc: boolean;
  iccat: boolean;
  iphc: boolean;
  wcpfc: boolean;
}

const INITIAL_RFMO_STATE: RfmoState = {
  iattc: false,
  iccat: false,
  iphc: false,
  wcpfc: false,
};

export default function InternationalFisheryMgmtAreasDataLayer(
  props: RegistryComponentProps
): React.ReactElement {
  const { slug, isReady, isActive, setActive, mapController } = useDataLayer(
    props
  );
  const [rfmoState, setRfmoState] = useState<RfmoState>(INITIAL_RFMO_STATE);
  const syncedFromLayoutRef = useRef(false);

  // When layer is in layout, mapController has it active but our isActive may not have synced yet.
  useLayoutEffect(() => {
    if (syncedFromLayoutRef.current || !setActive || !mapController?.isReady())
      return;
    if (mapController.isLayerActive(slug) && !isActive) {
      setActive(true);
      syncedFromLayoutRef.current = true;
    }
  }, [slug, isActive, setActive, mapController]);

  useEffect(() => {
    if (!mapController?.isReady()) return;
    setRfmoState({
      iattc: mapController.isLayerActive('iattc'),
      iccat: mapController.isLayerActive('iccat'),
      iphc: mapController.isLayerActive('iphc'),
      wcpfc: mapController.isLayerActive('wcpfc'),
    });
  }, [mapController, isActive]);

  const handleRfmoToggle = async (rfmoSlug: RfmoSlug): Promise<void> => {
    if (!mapController?.isReady()) return;
    try {
      await mapController.toggleLayer(rfmoSlug);
      setRfmoState((prev) => ({
        ...prev,
        [rfmoSlug]: mapController.isLayerActive(rfmoSlug),
      }));
    } catch (e: unknown) {
      console.error('Toggle RFMO layer failed:', e);
    }
  };

  const rfmoAvailable = true;

  return (
    <div className="control-section">
      <div className="section-title">
        <span className="section-icon">
          International Fishery Management Areas
        </span>
      </div>
      <div
        style={{
          fontSize: '12px',
          color: '#6c757d',
          marginBottom: '12px',
          fontStyle: 'italic',
        }}
      >
        Regional Fisheries Management Organizations (RFMO) boundaries
      </div>
      <div className="form-group">
        <label
          className="checkbox-label"
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setActive(e.target.checked)
            }
            disabled={!isReady}
            style={{ marginRight: '8px' }}
          />
          <span>Show International Fishery Management Areas</span>
        </label>
      </div>
      {isActive && (
        <div
          style={{
            marginLeft: '20px',
            marginTop: '8px',
            paddingLeft: '8px',
            borderLeft: '2px solid #e5e7eb',
          }}
        >
          {RFMO_SLUGS.map((rfmoSlug) => {
            const def = RFMO_DEFS[rfmoSlug];
            const checked = rfmoState[rfmoSlug];
            return (
              <div key={rfmoSlug} style={{ marginBottom: '8px' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: rfmoAvailable ? 'pointer' : 'not-allowed',
                    color: '#1f2937',
                    fontSize: '12px',
                    gap: '8px',
                    padding: '4px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      rfmoAvailable && handleRfmoToggle(rfmoSlug)
                    }
                    disabled={!rfmoAvailable}
                    style={{ marginRight: '8px', transform: 'scale(1.1)' }}
                  />
                  <div>
                    <div
                      style={{
                        fontWeight: 'bold',
                        color: checked ? '#10b981' : '#1f2937',
                      }}
                    >
                      {def.name}
                    </div>
                    <div
                      style={{
                        fontSize: '10px',
                        color: '#6b7280',
                        marginTop: '2px',
                      }}
                    >
                      {def.description}
                    </div>
                  </div>
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
